import ReactDOM from "react-dom/client";
import {
	Route,
	createBrowserRouter,
	createRoutesFromElements,
	RouterProvider,
	Navigate,
	useNavigate,
	useRouteError,
	isRouteErrorResponse
} from "react-router-dom";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import LandingPageV2 from "./pages/Website/LandingPageV2";
import { createIDBPersister } from "./services/idb-persister/idbPersister";
import LoginPage from "./pages/App/LoginPage";
import AuthProvider from "./context/AuthProvider";
import PrivateRouteWrapper from "./components/PrivateRouteWrapper";
import renderPrivacy from "./pages/Legal/PrivacyPolicy";
import renderTerms from "./pages/Legal/TermsService";
import React from "react";
import AccountPage from "./pages/App/AccountPage";
import ButtonDemo from "./components/DesignSystem/Input/ButtonDemo";
import JobCardDemo from "./components/DesignSystem/Output/JobCardDemo";
import TabDemo from "./components/DesignSystem/Tabs/TabDemo";
import NormalizeURL from "./components/NormalizeURL";
import SwipeXDemo from "./components/DesignSystem/Gestures/SwipeXDemo";
import MenuDemo from "./components/DesignSystem/Menu/MenuDemo";
import { AppLayout } from "./components/DesignSystem/Layout/AppLayout";
import { WebsiteLayout } from "./components/DesignSystem/Layout/WebsiteLayout";
import DashboardPage from "./pages/App/DashboardPage";
import JobPage from "./pages/App/JobPage";
import { OutputDemo } from "./components/DesignSystem/Output/OutputDemo";
import ArchJobPage from "./pages/App/ArchJobPage";
import InputDemo from "./components/DesignSystem/Input/InputDemo";
import SnackbarDemo from "./components/DesignSystem/Snackbars/SnackbarDemo";
import { SnackbarProvider } from "./components/DesignSystem/Snackbars/SnackbarProvider";
import Mission from "./pages/Website/Mission";
import Careers from "./pages/Website/Careers";
import NotFound from "./pages/Website/NotFound";
import XmlSiteMap from "./pages/Website/XmlSiteMap";
import { LinkedInCallback } from "react-linkedin-login-oauth2";
import "./index.css";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			// 24 hours. See: https://tanstack.com/query/latest/docs/framework/react/plugins/persistQueryClient/#how-it-works
			gcTime: 1000 * 60 * 60 * 24
		}
	}
});

const persister = createIDBPersister();

//TODO: refactor this out of the page.
const RootErrorBoundary: React.FC = () => {
	const navigate = useNavigate();
	const error = useRouteError();
	if (isRouteErrorResponse(error)) {
		// Redirect to 404 Page
		if (error.status === 404) {
			window.location.replace("/404");
			return <></>;
		}
	}
	// Generic error message
	return (
		<div className="text-center p-10">
			<img
				className="w-60 inline-block mb-4"
				src={process.env.PUBLIC_URL + "/MappyIcon.png"}
				alt="Mappy"
			/>
			<h1 className="text-3xl">Oops! Something went wrong!</h1>
			<button className="underline text-lg" onClick={() => navigate(0)}>
				Click here to reload
			</button>
		</div>
	);
};

const ReferralRedirect: React.FC = () => {
	const params = new URL(window.location.href).pathname.split("/");
	const code = params[params.length - 1];
	return <Navigate to={`/landing?ref=${encodeURIComponent(code)}`} replace />;
};

const isDebug = process.env.NODE_ENV === "development";

//https://reactrouter.com/start/declarative/routing
function Main() {
	console.log(`URL:[${window.location}]`);
	const browserRouter = createBrowserRouter(
		createRoutesFromElements(
			<Route path="/" errorElement={<RootErrorBoundary />}>
				{/*App Routes 2.0*/}
				<Route element={<AppLayout />}>
					{/* TODO: PrivateRouteWrapper --> Rename to loggedin */}
					<Route element={<PrivateRouteWrapper />}>
						<Route path="dashboard" element={<DashboardPage />} />
					</Route>

					{/*ArchPage 2.0*/}
					<Route path="arc" element={<PrivateRouteWrapper />}>
						<Route index element={<ArchJobPage />} />
					</Route>
				</Route>

				{/*JobPage 2.0 - No Nav*/}
				<Route element={<AppLayout showNav={false} />}>
					{/* TODO: PrivateRouteWrapper --> Rename to loggedin */}
					<Route element={<PrivateRouteWrapper />}>
						<Route path="job/:jobId" element={<JobPage />} />
					</Route>
				</Route>

				{/*WebSite 2.0 */}
				<Route element={<WebsiteLayout />}>
					<Route path="newlanding" element={<LandingPageV2 />} />
					<Route path="landing" element={<LandingPageV2 />} />
					<Route path="mission" element={<Mission />} />
					<Route path="careers" element={<Careers />} />
					<Route path="terms" element={renderTerms()} />
					<Route path="privacy" element={renderPrivacy()} />
					<Route path="404" element={<NotFound />} />
				</Route>

				{/* Referral redirect */}
				<Route element={<WebsiteLayout />}>
					<Route path="r/:code" element={<ReferralRedirect />} />
				</Route>

				{/* No layout routes */}
				<Route index element={<Navigate to="landing" replace />} />
				<Route path="login" element={<LoginPage />} />
				<Route path="linkedin-login" element={<LinkedInCallback />} />
				<Route path="sitemap.xml" element={<XmlSiteMap />} />

				{/* Debug routes - no layout */}
				{isDebug && (
					<>
						<Route path="output" element={<OutputDemo />} />
						<Route path="account" element={<AccountPage />} />
						<Route path="btn" element={<ButtonDemo />} />
						<Route path="input" element={<InputDemo />} />
						<Route path="tab" element={<TabDemo />} />
						<Route path="jobcard" element={<JobCardDemo />} />
						<Route path="menu" element={<MenuDemo />} />
						<Route path="gesture" element={<SwipeXDemo />} />
						<Route path="snack" element={<SnackbarDemo />} />
					</>
				)}

				{/* Catch-all redirect for capitalization */}
				<Route path="*" element={<NormalizeURL />} />
			</Route>
		)
	);
	return (
		<React.StrictMode>
			<PersistQueryClientProvider
				client={queryClient}
				persistOptions={{ persister }}
			>
				<AuthProvider>
					<SnackbarProvider>
						<RouterProvider router={browserRouter} />
					</SnackbarProvider>
				</AuthProvider>
			</PersistQueryClientProvider>
		</React.StrictMode>
	);
}

ReactDOM.createRoot(document.getElementById("root")!).render(<Main />);
